import React from 'react';
import { motion } from 'framer-motion';
import footer from '../../assets/footer.png'
import { Link, useNavigate } from 'react-router-dom';

const getCurrentYear = () => new Date().getFullYear();

const Footer = () => {
    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.2
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                type: 'spring',
                stiffness: 100,
                damping: 10
            }
        }
    };

    const marqueeVariants = {
        animate: {
            x: ['0%', '-100%'],
            transition: {
                x: {
                    repeat: Infinity,
                    repeatType: 'loop',
                    duration: 40,
                    ease: 'linear'
                }
            }
        }
    };

    const scrollTo = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'start' // Aligns to top of viewport
            });
        }
    };




    // Data for reusable components with external link support
    const navItems = [
        { id: 'HeroSection', label: 'Home' },
        // {
        //     id: 'training',
        //     label: 'Training',
        //     type: 'route',       // indicates it's an external link
        //     url: 'https://www.training.vorstel.com/'
        // },
        { id: 'Consulting', label: 'Consulting' },
        { id: 'DesignServices', label: 'Services' },
        { id: 'Gen', label: 'Gen AI & ML' },
        { id: 'AboutSection', label: 'About us' },
        { id: 'ContactUs', label: 'Contact' },
        { id: 'Feeds', label: 'Feeds', type: 'route', url: '/feeds' },
        // { id: 'Resources', label: 'Resources', type: 'route', url: '/resources' }


    ];
    const marqueeItems = [
        'Subscribe to our newsletter',
        'How it works',
        'Documentation',
        'Join the community',
        'Software solutions',
        'Consulting services',
        'Case studies',
        'Request a demo',
        'Talk to an expert'
    ];

    const legalLinks = [
        { name: 'Terms', url: '/terms' },
        { name: 'Privacy', url: '/privacy' },
        { name: 'Cookies', url: '/cookies' }
    ];
    return (
        <footer className="bg-black text-black border-t border-gray-800">
            {/* Top Scrolling Banner */}
            <div className="w-full bg-white text-black py-3 overflow-hidden relative">
                <div className="absolute inset-0 bg-gradient-to-r from-white via-transparent to-white z-10 w-full" />
                <motion.div
                    className="inline-flex items-center whitespace-nowrap"
                    variants={marqueeVariants}
                    animate="animate"
                    aria-hidden="true"
                >
                    {[...marqueeItems, ...marqueeItems].map((item, index) => (
                        <React.Fragment key={`${item}-${index}`}>
                            <span className="mx-6 text-sm font-medium">{item}</span>
                            {index < marqueeItems.length * 2 - 1 && (
                                <span className="text-gray-400">•</span>
                            )}
                        </React.Fragment>
                    ))}
                </motion.div>
            </div>

            {/* Main Footer Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <motion.div
                    className="grid grid-cols-1 md:grid-cols-12 gap-8"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={containerVariants}
                >
                    {/* Logo & Company Info - Centered on mobile */}
                    <motion.div
                        className="md:col-span-4 lg:col-span-3 text-center md:text-left"
                        variants={itemVariants}
                    >
                        <div className="flex justify-center md:justify-start items-center mb-4">
                            <img
                                // src={header}
                                src={footer}
                                alt="Vorstel logo"
                                width="150"
                                height="60"
                                style={{
                                    maxWidth: '150px',
                                }}
                                className="max-w-full max-h-full object-contain p-0.5"
                            />

                        </div>
                        <p className="text-gray-400 text-sm mt-2 mx-auto md:mx-0 max-w-xs">
                            Vorstel embodies conviction, experience, and agility—empowering clients to achieve success through cutting-edge information technology
                        </p>
                    </motion.div>

                    {/* Navigation - Centered on mobile */}
                    <motion.div
                        className="md:col-span-4 lg:col-span-2 text-center md:text-left"
                        variants={itemVariants}
                    >
                        <h3 className="text-sm font-semibold uppercase tracking-wider mb-4">
                            Navigation
                        </h3>

                        <nav className="space-y-2 flex flex-col items-center md:items-start">
                            {navItems.map((item) => {
                                if (item.type === 'route') {
                                    return (
                                        <motion.a
                                            key={item.id}
                                            href={item.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-gray-400 hover:text-white transition-colors text-sm"
                                            whileHover={{ x: 5 }}
                                            variants={itemVariants}
                                        >
                                            {item.label}
                                        </motion.a>
                                    );
                                }

                                return (
                                    <motion.a
                                        key={item.id}
                                        href={`#${item.id}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            scrollTo(item.id);
                                        }}
                                        className="text-gray-400 hover:text-white transition-colors text-sm"
                                        whileHover={{ x: 5 }}
                                        variants={itemVariants}
                                    >
                                        {item.label}
                                    </motion.a>
                                );
                            })}
                        </nav>
                    </motion.div>


                    {/* Contact Info - Centered on mobile */}
                    <motion.div
                        className="md:col-span-4 lg:col-span-3 text-center md:text-left"
                        variants={itemVariants}
                    >
                        <h3 className="text-sm font-semibold uppercase tracking-wider mb-4">
                            Contact
                        </h3>
                        <address className="not-italic text-gray-400 text-sm space-y-2 flex flex-col items-center md:items-start">
                            <motion.p variants={itemVariants}>
                                Richard-Wagner-Straße 4,<br />
                                68165, Mannheim, Deutschland
                            </motion.p>
                            <motion.a
                                href="mailto:sales@vorstel.com"
                                className="hover:text-white transition-colors"
                                variants={itemVariants}
                            >
                                sales@vorstel.com
                            </motion.a>
                            <motion.a
                                href="mailto:contact@vorstel.com"
                                className="hover:text-white transition-colors"
                                variants={itemVariants}
                            >
                                contact@vorstel.com
                            </motion.a>
                            <motion.a
                                href="https://wa.me/4915510324428"
                                className="hover:text-white transition-colors"
                                variants={itemVariants}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                +49-15510-324428
                            </motion.a>

                            {/* VAT & Tax Info */}
                            <motion.div className="mt-4 space-y-1 text-gray-400 text-xs" variants={itemVariants}>
                                <p>UStIdNr: DE459178797</p>
                                <p>Steuernummer: 38189/57039</p>
                            </motion.div>
                        </address>
                    </motion.div>

                    {/* Newsletter - Centered on mobile */}
                    <motion.div
                        className="md:col-span-12 lg:col-span-4 mt-8 lg:mt-0 text-center md:text-left"
                        variants={itemVariants}
                    >
                        <h3 className="text-sm font-semibold uppercase tracking-wider mb-4">
                            Newsletter
                        </h3>
                        <p className="text-gray-400 text-sm mb-4 mx-auto md:mx-0 max-w-md">
                            Subscribe to our newsletter for the latest updates and news.
                        </p>
                        <form className="flex flex-col sm:flex-row gap-2 items-center md:items-start">
                            <motion.div
                                className="w-full sm:max-w-xs"
                                whileFocus={{ scale: 1.02 }}
                                transition={{ type: 'spring', stiffness: 300 }}
                            >
                                <label htmlFor="email" className="sr-only">Email address</label>
                                <input
                                    id="email"
                                    type="email"
                                    placeholder="Enter your email"
                                    className="w-full px-4 py-2 rounded-lg border border-gray-700 bg-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-600 text-sm"
                                    required
                                />
                            </motion.div>
                            <motion.button
                                type="submit"
                                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors w-full sm:w-auto"
                                whileHover={{ scale: 1.03 }}
                                whileTap={{ scale: 0.97 }}
                            >
                                Subscribe
                            </motion.button>

                        </form>
                        {/* Disclaimer / Privacy */}
                        <motion.div className="mt-4 space-y-2 text-gray-400 text-xs" variants={itemVariants}>
                            <p>
                                Disclaimer / Liability Notice: The information provided on this website is for general informational purposes only. While we strive to keep the content accurate and up-to-date, Vorstel GmbH assumes no liability for any errors or omissions.
                            </p>
                            <p>
                                Privacy Notice: Please refer to our Privacy Policy for details on how we collect, process, and protect personal data in accordance with GDPR.
                            </p>
                        </motion.div>
                    </motion.div>
                </motion.div>
            </div>

            {/* Footer Bottom - Centered on mobile */}
            <motion.div
                className="border-t border-gray-800 py-6"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center text-center md:text-left">
                    <div className="text-gray-400 text-sm mb-4 md:mb-0">
                        © {new Date().getFullYear()} Vorstel.  All rights reserved.
                    </div>
                    {/* // In your JSX: */}
                    <div className="flex space-x-6 justify-center">
                        {legalLinks.map((item) => (
                            <motion.span
                                key={item.name}
                                onClick={() => window.open(item.url, '_blank', 'noopener,noreferrer')}
                                className="cursor-pointer text-gray-400 hover:text-white text-sm transition-colors"
                                whileHover={{ y: -2 }}
                            >
                                {item.name}
                            </motion.span>
                        ))}
                    </div>
                </div>
            </motion.div>
        </footer>
    );
};

export default Footer;